import { useState, useEffect, useCallback } from 'react';
import { WorkEntry, User, Team, Project, Location } from '../types';
import { apiService, PagedResult, PaginationOptions } from '../services/api';

interface AdminData {
  workEntries: WorkEntry[];
  allUsers: User[];
  teams: Team[];
  projects: Project[];
  locations: Location[];
  pagination?: {
    totalCount: number;
    currentPage: number;
    totalPages: number;
    pageSize: number;
  };
}

interface UseAdminDataReturn {
  data: AdminData;
  isLoading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  loadNextPage: () => Promise<void>;
  loadPreviousPage: () => Promise<void>;
  setPageSize: (size: number) => void;
  updateWorkEntry: (workEntryId: number, updatedEntry: WorkEntry) => void;
  addWorkEntry: (newEntry: WorkEntry) => void;
  removeWorkEntry: (workEntryId: number) => void;
  updateUser: (userId: number, updatedUser: User) => void;
  addUser: (newUser: User) => void;
  removeUser: (userId: number) => void;
  addProject: (newProject: Project) => void;
  updateProject: (projectId: number, updatedProject: Project) => void;
  removeProject: (projectId: number) => void;
  addLocation: (newLocation: Location) => void;
  updateLocation: (locationId: number, updatedLocation: Location) => void;
  removeLocation: (locationId: number) => void;
}

export const useAdminData = (selectedDate: string): UseAdminDataReturn => {
  const [data, setData] = useState<AdminData>({ workEntries: [], allUsers: [], teams: [], projects: [], locations: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Load data with pagination
  const loadData = useCallback(async (page?: number) => {
    setError(null);
    setIsLoading(true);
    
    const targetPage = page ?? currentPage;

    try {
      // Fetch data in parallel
      const [workEntriesResponse, allUsersResponse, teamsResponse, projectsResponse, locationsResponse] = await Promise.all([
        apiService.getWorkEntries(selectedDate, undefined, { page: targetPage, pageSize }),
        apiService.getAllUsers(),
        apiService.getTeams(),
        apiService.getProjects(),
        apiService.getLocations()
      ]);

      // Transform work entries from paginated response
      const workEntries = Array.isArray(workEntriesResponse.data) ? workEntriesResponse.data.map(entry => ({
        id: entry.id,
        userId: entry.userId,
        userName: entry.userName,
        projectId: entry.projectId,
        projectName: entry.projectName,
        locationId: entry.locationId,
        locationName: entry.locationName,
        startTime: entry.startTime,
        endTime: entry.endTime,
        breakMinutes: entry.breakMinutes,
        comments: entry.comments,
        workDescription: entry.workDescription,
        voiceRecording: entry.voiceRecording,
        date: entry.date,
        isActive: entry.isActive,
        totalBreakMinutes: entry.totalBreakMinutes,
        breaks: entry.breaks,
        activeBreak: entry.activeBreak
      })) : [];

      const newData: AdminData = {
        workEntries,
        allUsers: Array.isArray(allUsersResponse) ? allUsersResponse : [],
        teams: Array.isArray(teamsResponse) ? teamsResponse : [],
        projects: Array.isArray(projectsResponse) ? projectsResponse : [],
        locations: Array.isArray(locationsResponse) ? locationsResponse : [],
        pagination: {
          totalCount: workEntriesResponse.totalCount,
          currentPage: workEntriesResponse.page,
          totalPages: workEntriesResponse.totalPages,
          pageSize: workEntriesResponse.pageSize
        }
      };

      setData(newData);
      setCurrentPage(targetPage);

    } catch (err) {
      console.error('Error loading admin data:', err);
      
      let errorMessage = 'Eroare la încărcarea datelor';
      if (err instanceof Error) {
        if (err.message.includes('500')) {
          errorMessage = 'Server temporar indisponibil';
        } else if (err.message.includes('Failed to fetch')) {
          errorMessage = 'Probleme de conectivitate';
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, currentPage, pageSize]);

  // Simple refresh without debouncing
  const refreshData = useCallback(async () => {
    await loadData();
  }, [loadData]);

  // Pagination controls
  const loadNextPage = useCallback(async () => {
    if (data.pagination && currentPage < data.pagination.totalPages) {
      await loadData(currentPage + 1);
    }
  }, [loadData, currentPage, data.pagination]);

  const loadPreviousPage = useCallback(async () => {
    if (currentPage > 1) {
      await loadData(currentPage - 1);
    }
  }, [loadData, currentPage]);

  const handleSetPageSize = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  }, []);

  // Optimistic updates for better UX
  const updateWorkEntry = useCallback((workEntryId: number, updatedEntry: WorkEntry) => {
    // Update local state immediately
    setData(prev => ({
      ...prev,
      workEntries: Array.isArray(prev.workEntries) ? prev.workEntries.map(entry => 
        entry.id === workEntryId ? updatedEntry : entry
      ) : []
    }));
  }, []);

  const addWorkEntry = useCallback((newEntry: WorkEntry) => {
    setData(prev => ({
      ...prev,
      workEntries: Array.isArray(prev.workEntries) ? [...prev.workEntries, newEntry] : [newEntry]
    }));
  }, []);

  const removeWorkEntry = useCallback((workEntryId: number) => {
    setData(prev => ({
      ...prev,
      workEntries: Array.isArray(prev.workEntries) ? prev.workEntries.filter(entry => entry.id !== workEntryId) : []
    }));
  }, []);

  const updateUser = useCallback((userId: number, updatedUser: User) => {
    setData(prev => ({
      ...prev,
      allUsers: Array.isArray(prev.allUsers) ? prev.allUsers.map(user => 
        user.id === userId ? updatedUser : user
      ) : []
    }));
  }, []);

  const addUser = useCallback((newUser: User) => {
    setData(prev => ({
      ...prev,
      allUsers: Array.isArray(prev.allUsers) ? [...prev.allUsers, newUser] : [newUser]
    }));
  }, []);

  const removeUser = useCallback((userId: number) => {
    setData(prev => ({
      ...prev,
      allUsers: Array.isArray(prev.allUsers) ? prev.allUsers.filter(user => user.id !== userId) : [],
      workEntries: Array.isArray(prev.workEntries) ? prev.workEntries.filter(entry => entry.userId !== userId) : []
    }));
  }, []);

  // Project management methods
  const addProject = useCallback((newProject: Project) => {
    setData(prev => ({
      ...prev,
      projects: Array.isArray(prev.projects) ? [...prev.projects, newProject] : [newProject]
    }));
  }, []);

  const updateProject = useCallback((projectId: number, updatedProject: Project) => {
    setData(prev => ({
      ...prev,
      projects: Array.isArray(prev.projects) ? prev.projects.map(project => 
        project.id === projectId ? updatedProject : project
      ) : []
    }));
  }, []);

  const removeProject = useCallback((projectId: number) => {
    setData(prev => ({
      ...prev,
      projects: Array.isArray(prev.projects) ? prev.projects.filter(project => project.id !== projectId) : []
    }));
  }, []);

  // Location management methods
  const addLocation = useCallback((newLocation: Location) => {
    setData(prev => ({
      ...prev,
      locations: Array.isArray(prev.locations) ? [...prev.locations, newLocation] : [newLocation]
    }));
  }, []);

  const updateLocation = useCallback((locationId: number, updatedLocation: Location) => {
    setData(prev => ({
      ...prev,
      locations: Array.isArray(prev.locations) ? prev.locations.map(location => 
        location.id === locationId ? updatedLocation : location
      ) : []
    }));
  }, []);

  const removeLocation = useCallback((locationId: number) => {
    setData(prev => ({
      ...prev,
      locations: Array.isArray(prev.locations) ? prev.locations.filter(location => location.id !== locationId) : []
    }));
  }, []);

  // Initial load without auto-refresh
  // Single effect to handle all data loading
  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    data,
    isLoading,
    error,
    refreshData,
    loadNextPage,
    loadPreviousPage,
    setPageSize: handleSetPageSize,
    updateWorkEntry,
    addWorkEntry,
    removeWorkEntry,
    updateUser,
    addUser,
    removeUser,
    addProject,
    updateProject,
    removeProject,
    addLocation,
    updateLocation,
    removeLocation
  };
};